import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from '@mui/material';
import Logo from '../../components/common/Logo';
import { offerDetails } from '../../services/api';

interface OfferData {
  id: number;
  title: string;
  image: string;
  price: string;
}

interface BuyerFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface Tokens {
  accessToken?: string;
  refreshToken?: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  captcha?: string;
}

const BuyerDetails: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [offerLoading, setOfferLoading] = useState(true);
  const [formData, setFormData] = useState<BuyerFormData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!captchaToken) {
      newErrors.captcha = 'Please complete the captcha';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for the field being changed
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: undefined }));
    }
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    setApiError(null);
    setLoading(true);
    try {
      const response = await offerDetails.registerGuestUser(
        formData.firstName,
        formData.lastName,
        formData.email,
        captchaToken || ''
      );
      
      if (response.success) {
        // Navigate to payment page with tokens
        navigate(`/offer/${offerCode}/payment`, { 
          state: { 
            offerData,
            tokens: response.data?.tokens,
            email: formData.email
          } 
        });
      } else {
        setApiError(response.message || 'Failed to submit buyer details');
        // Clear form fields and reset captcha on error
        setFormData({ firstName: '', lastName: '', email: '' });
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to submit buyer details';
      setApiError(errorMessage);
      // Clear form fields and reset captcha on error
      setFormData({ firstName: '', lastName: '', email: '' });
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const fetchOfferDetails = async () => {
      if (!offerCode) {
        return;
      }

      // Check if offerData is passed from previous page
      if (location.state?.offerData) {
        setOfferData({
          id: location.state.offerData.id,
          title: location.state.offerData.title,
          image: location.state.offerData.image,
          price: location.state.offerData.price,
        });
        setOfferLoading(false);
        return;
      }

      try {
        const response = await offerDetails.getOfferByIdForGuestUser(offerCode);
        if (response.success) {
          setOfferData({
            id: response.data.id,
            title: response.data.title,
            image: response.data.image,
            price: response.data.price,
          });
        }
      } catch (err) {
        console.error('Error fetching offer details:', err);
      } finally {
        setOfferLoading(false);
      }
    };

    fetchOfferDetails();
  }, [offerCode, location.state]);

  const handleBack = () => {
    navigate(`/offer/${offerCode}`);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
   
      {/* Logo at the top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {offerLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {offerData && (
              <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {offerData.image && (
                    <Card sx={{ width: 100, height: 100, flexShrink: 0 }}>
                      <CardMedia
                        component="img"
                        sx={{ height: 100, objectFit: 'cover' }}
                        image={offerData.image}
                        alt={offerData.title}
                      />
                    </Card>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {offerData.title}
                    </Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      ${offerData.price}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Buyer Details
            </Typography>

            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {apiError}
              </Alert>
            )}

            <Box component="form" noValidate>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                sx={{ mb: 3 }}
              />

              <Box sx={{ mb: 3 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                  onChange={handleCaptchaChange}
                />
                {errors.captcha && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.captcha}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleContinue}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
              </Button>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleBack}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Back
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default BuyerDetails;
